import gql from 'graphql-tag';

const FRAGMENTS = gql`
  fragment PaymentCategory on payment_categories {
    id
    value: id
    label
    type
  }
  fragment UserPaymentRate on user_payment_rates {
    id
    user_id
    rate_type
    rate
    payment_category {
      ...PaymentCategory
    }
  }
  fragment LineItem on payment_line_items {
    id
    date
    student_id
    student {
      user_first_name
      user_last_name
    }
    group {
      name
    }
    comments
    approved
    approved_by
    created_at
    duration
    payment_category_id
    payment_category {
      ...PaymentCategory
    }
    status
    user_id
    total
  }
`;

export const GET_CATEGORIES = gql`
  query {
    payment_categories(
      where: { deleted: { _eq: false } }
      order_by: { label: asc }
    ) {
      ...PaymentCategory
    }
  }
  ${FRAGMENTS}
`;

export const ADD_CATEGORY = gql`
  mutation addCategory($data: payment_categories_insert_input!) {
    insert_payment_categories_one(object: $data) {
      ...PaymentCategory
    }
  }
  ${FRAGMENTS}
`;

export const EDIT_CATEGORY = gql`
  mutation updateCategory($id: Int!, $data: payment_categories_set_input!) {
    update_payment_categories_by_pk(pk_columns: { id: $id }, _set: $data) {
      ...PaymentCategory
    }
  }
  ${FRAGMENTS}
`;

export const GET_COACHES = gql`
  query getUsers($role: String!) {
    users(
      where: {
        user_type: { _eq: $role }
        _or: [{ deleted: { _is_null: true } }, { deleted: { _neq: true } }]
      }
    ) {
      id: user_id
      user_pic
      user_first_name
      user_last_name
      user_type
      payment_account {
        payouts_enabled
      }
      user_payment_rates_aggregate {
        aggregate {
          count
        }
      }
      payment_line_items_aggregate(
        where: { status: { _eq: "approved" }, invoice_id: { _is_null: true } }
      ) {
        aggregate {
          sum {
            total
          }
        }
      }
    }
  }
`;

export const GET_ALL_COACHES = gql`
  query getUsers {
    users(
      where: {
        _and: [
          {
            _or: [
              { user_type: { _eq: "lead_coach" } }
              { user_type: { _eq: "head_coach" } }
              { user_type: { _eq: "fa_coach" } }
            ]
          }
          {
            _or: [{ deleted: { _is_null: true } }, { deleted: { _neq: true } }]
          }
        ]
      }
    ) {
      id: user_id
      user_pic
      user_first_name
      user_last_name
      user_type
      payment_account {
        payouts_enabled
      }
      user_payment_rates_aggregate {
        aggregate {
          count
        }
      }
      payment_line_items_aggregate(
        where: { status: { _eq: "approved" }, invoice_id: { _is_null: true } }
      ) {
        aggregate {
          sum {
            total
          }
        }
      }
    }
  }
`;

export const UPSERT_RATES = gql`
  mutation upsertRates($data: [user_payment_rates_insert_input!]!) {
    insert_user_payment_rates(
      objects: $data
      on_conflict: {
        constraint: user_payment_rates_user_id_category_id_key
        update_columns: [rate, rate_type]
      }
    ) {
      affected_rows
    }
  }
`;

export const REMOVE_RATES = gql`
  mutation removeRates($ids: [Int!]!) {
    delete_user_payment_rates(where: { id: { _in: $ids } }) {
      affected_rows
    }
  }
`;

export const GTE_USER_RATES = gql`
  query getUserRates($userId: Int!) {
    rows: user_payment_rates(where: { user_id: { _eq: $userId } }) {
      ...UserPaymentRate
    }
  }
  ${FRAGMENTS}
`;

export const GET_USER_RATE = gql`
  query getUserRate($userId: Int!, $categoryId: Int!) {
    rows: user_payment_rates(
      where: { user_id: { _eq: $userId }, category_id: { _eq: $categoryId } }
    ) {
      rate
      rate_type
    }
  }
`;

export const INSERT_PAYMENT_ITEM = gql`
  mutation insPaymentItem($data: [payment_line_items_insert_input!]!) {
    insert_payment_line_items(objects: $data) {
      affected_rows
    }
  }
`;

export const UPDATE_PAYMENT_ITEM = gql`
  mutation updatePaymentItem($id: Int!, $data: payment_line_items_set_input!) {
    update_payment_line_items_by_pk(pk_columns: { id: $id }, _set: $data) {
      ...LineItem
    }
  }
  ${FRAGMENTS}
`;

export const UPDATE_PAYMENT_ITEMS = gql`
  mutation updatePaymentItemsStatus($ids: [Int!]!, $data: payment_line_items_set_input!) {
    update_payment_line_items(
      where: { id: { _in: $ids } }
      _set: $data
    ) {
      returning {
        id
        status
      }
    }
  }
`;

export const DELETE_PAYMENT_ITEM = gql`
  mutation deletePaymentItem($id: Int!) {
    delete_payment_line_items_by_pk(id: $id) {
      updated_at
    }
  }
`;

export const GET_USER_PAYMENT_ITEMS = gql`
  query getUserPaymentItems($userId: Int!, $status: [String!]) {
    rows: payment_line_items(
      where: {
        user_id: { _eq: $userId }
        status: { _in: $status }
        invoice_id: { _is_null: true }
      }
      order_by: [{ created_at: desc }]
    ) {
      ...LineItem
    }
  }
  ${FRAGMENTS}
`;

export const GET_PAYMENT_ITEMS = gql`
  query getPaymentItems($status: String) {
    rows: payment_line_items(
      where: { status: { _eq: $status } }
      order_by: [{ created_at: desc }]
    ) {
      user {
        user_first_name
        user_last_name
      }
      ...LineItem
    }
  }
  ${FRAGMENTS}
`;

export const GET_PAYMENT_ITEMS_FOR_BONUS = gql`
  query getLineItems($userId: Int!, $categories: [String!]!) {
    rows: payment_line_items(
      where: {
        user_id: { _eq: $userId }
        status: { _eq: "approved" }
        student_id: { _is_null: false }
        payment_category: { type: { _in: $categories } }
        bonus_line_id: { _is_null: true }  
      }
    ) {
      id
      duration
      date
      total
      student_id  
      payment_category {
        label
        type  
        id
      }
      student {
        user_last_name
        user_first_name
      }
    }
    rates: user_payment_rates(
      where: {
        user_id: { _eq: $userId }
        payment_category: { type: { _in: $categories } }
      }
    ) {
      rate
      rate_type
      category_id
    }
    bonus_rates: user_payment_rates(
      where: {
        user_id: { _eq: $userId }
        payment_category: { type: { _eq: "cb" } }
      }
    ) {
      id
      category_id
      rate
      rate_type
      payment_category {
        label
      }
    }
  }
`;

export const GET_BONUS_HISTORY = gql`
    query getBonusHistory($userId: Int!) {
        rows: payment_line_items(where: {
            user_id: { _eq: $userId },
            payment_category: {
                type: {_eq: "cb"}
            }
            payment_line_items: {
                student_id: {_is_null: false}
            }
        }) {
            id
            date
            duration
            total
            comments
            status
            payment_line_items {
                id
                date
                duration
                total
                comments
                status
                student {
                    user_first_name
                    user_last_name
                }
                payment_invoice {
                    paid_at
                    status
                }
            }
            payment_invoice {
                paid_at
                status
            }
        }
    }
`;

export const CREATE_BONUS_ITEM = gql`
  mutation insPaymentItem(
    $data: payment_line_items_insert_input!
  ) {
    row: insert_payment_line_items_one(object: $data) {
      id
    }
  }
`;

export const GET_USER_INVOICES = gql`
  query getInvoices($userId: Int) {
    rows: payment_invoices(
      where: { user_id: { _eq: $userId } }
      order_by: [{ created_at: desc }]
    ) {
      id
      created_at
      last_attempt_error
      paid_at
      status
      total
    }
  }
`;

export const GET_PAYMENT_ACCOUNT = gql`
  query getPaymentAccount($userId: Int) {
    payment_accounts(where: { user_id: { _eq: $userId } }) {
      payouts_enabled
    }
  }
`;

export const GET_COACH = gql`
  query getUserById($id: Int!) {
    user: users_by_pk(user_id: $id) {
      user_first_name
      user_last_name
      payment_account {
        payouts_enabled
      }
    }
  }
`;

export const GET_INVOICE_DETAILS = gql`
  query getInvoice($id: uuid!) {
    invoice: payment_invoices_by_pk(id: $id) {
      id
      total
      created_at
      paid_at
      status
      payment_line_items {
        ...LineItem
      }
    }
  }
  ${FRAGMENTS}
`;
